import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Event,
  AccessTime
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { streamService } from '../services/streamService';

const Events = () => {
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => streamService.getEvents(),
  });

  const events = eventsData?.data?.events || [];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = (eventDate) => {
    return new Date(eventDate) >= new Date();
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#8B5CF6' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily: 'Orbitron',
            fontWeight: 900,
            background: 'linear-gradient(45deg, #8B5CF6, #06B6D4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Eventos
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.7 }}>
          Confira os próximos eventos da NostalBeat
        </Typography>
      </Box>

      {events.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Event sx={{ fontSize: 80, color: 'rgba(255, 255, 255, 0.3)', mb: 3 }} />
            <Typography variant="h5" sx={{ opacity: 0.7, mb: 2 }}>
              Nenhum evento encontrado
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.5 }}>
              Novos eventos serão anunciados em breve
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {events.map((event) => (
            <Grid item xs={12} sm={10} md={8} lg={6} xl={4} key={event.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(157, 0, 255, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {event.banner_image && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={event.banner_image}
                    alt={event.title}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/img/bg.png';
                    }}
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1, p: 3, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        lineHeight: 1.2,
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    {event.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {event.description}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Event sx={{ fontSize: 16, color: '#8B5CF6', mr: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(event.event_date)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTime sx={{ fontSize: 16, color: '#00D4FF', mr: 1 }} />
                      <Typography variant="body2">
                        {formatTime(event.start_time)} - {formatTime(event.end_time)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={isUpcoming(event.event_date) ? 'Em Breve' : 'Realizado'}
                      color={isUpcoming(event.event_date) ? 'primary' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                      }}
                    />
                    
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#8B5CF6',
                        color: '#8B5CF6',
                        '&:hover': {
                          borderColor: '#cccccc',
                          bgcolor: 'rgb(7, 105, 36)',
                        },
                      }}
                    >
                      Saiba Mais
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Events;
